import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { ADMIN_STATS_QUERY } from '../../graphql/queries';
import { useAuth } from '../../contexts/AuthContext';
import { sectionsFor } from '../../utils/staff';
import styles from './AdminDashboard.module.css';

const formatStorage = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const sections = sectionsFor(user);
  // Platform-wide totals are a super admin's view; the server refuses them to admins too.
  const { data, loading } = useQuery(ADMIN_STATS_QUERY, { skip: !isSuperAdmin });
  const stats = data?.adminStats;

  const statCards = [
    { label: 'Total Users', value: stats?.totalUsers ?? '—', icon: 'group', color: 'bg-blue-50 text-blue-600', action: () => navigate('/admin/users') },
    { label: 'Active Users', value: stats?.activeUsers ?? '—', icon: 'person_check', color: 'bg-green-50 text-green-600', action: null },
    { label: 'Total Projects', value: stats?.totalProjects ?? '—', icon: 'folder_managed', color: 'bg-purple-50 text-purple-600', action: () => navigate('/admin/projects') },
    { label: 'Total Files', value: stats?.totalFiles ?? '—', icon: 'insert_drive_file', color: 'bg-orange-50 text-orange-600', action: null },
    { label: 'Total Storage', value: stats ? formatStorage(stats.totalStorage) : '—', icon: 'storage', color: 'bg-teal-50 text-teal-600', action: null },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Admin Dashboard</h2>
          <p className="text-on-surface-variant mt-1">Platform-wide overview and management controls.</p>
        </div>

        {!isSuperAdmin ? null : loading ? (
          <div className="flex items-center justify-center py-16 text-on-surface-variant">
            <span className="material-symbols-outlined animate-spin mr-2">refresh</span>
            Loading stats...
          </div>
        ) : (
          <div className={styles.statsGrid}>
            {statCards.map((card) => (
              <div
                key={card.label}
                className={`bg-white rounded-xl p-6 ${card.action ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
                onClick={card.action ?? undefined}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-on-surface-variant mb-1">{card.label}</p>
                    <p className="text-3xl font-bold text-on-surface font-headline">{card.value}</p>
                  </div>
                  <div className={`p-3 rounded-xl ${card.color}`}>
                    <span className="material-symbols-outlined text-2xl">{card.icon}</span>
                  </div>
                </div>
                {card.action && (
                  <p className="mt-3 text-xs text-primary font-medium flex items-center gap-1">
                    View all
                    <span className="material-symbols-outlined text-sm">arrow_forward</span>
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {sections.length === 0 && (
            <p className="text-on-surface-variant">No admin pages are open to you yet — a super admin chooses them.</p>
          )}
          {sections.map((section) => (
            <div
              key={section.to}
              className="bg-white rounded-xl p-6 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(section.to)}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className={`p-3 rounded-xl ${section.color}`}>
                  <span className="material-symbols-outlined text-2xl">{section.icon}</span>
                </div>
                <div>
                  <h3 className="font-bold text-on-surface">{section.title}</h3>
                  <p className="text-sm text-on-surface-variant">{section.description}</p>
                </div>
              </div>
              <p className="text-xs text-primary font-medium flex items-center gap-1">
                {section.cta}
                <span className="material-symbols-outlined text-sm">arrow_forward</span>
              </p>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
